<template>
  <div class="resource-page">
    <section class="resource-toolbar">
      <div>
        <h2>{{ title }}</h2>
        <p>{{ subtitle }}</p>
      </div>
      <div class="toolbar-actions">
        <v-btn v-if="canCreate" text color="primary" @click="openDialog()">
          <v-icon left size="18">mdi-plus</v-icon>
          {{ createButtonLabel }}
        </v-btn>
      </div>
    </section>

    <ListFilters
      v-model="filterDraft"
      :fields="filterFields"
      :loading="loading"
      @filter="applyFilters"
      @clear="applyFilters"
    />

    <section class="table-panel">
      <BoaruaDataTable
        :headers="tableHeaders"
        :items="filteredItems"
        :loading="loading"
        item-key="id"
        class="boarua-table"
      >
        <template v-slot:item.created_at="{ item }">
          {{ item.created_at | defaultDate }}
        </template>
        <template v-slot:item.starts_at="{ item }">
          {{ item.starts_at | defaultDate }}
        </template>
        <template v-slot:item.ends_at="{ item }">
          {{ item.ends_at ? $options.filters.defaultDate(item.ends_at) : "-" }}
        </template>
        <template v-slot:item.published_at="{ item }">
          {{ item.published_at ? formatDateTime(item.published_at) : "-" }}
        </template>
        <template v-slot:item.due_date="{ item }">
          {{ formatDate(item.due_date) }}
        </template>
        <template v-slot:item.reference_month="{ item }">
          {{ formatReferenceMonth(item.reference_month) }}
        </template>
        <template v-slot:item.unit_label="{ item }">
          {{ chargeUnitLabel(item) }}
        </template>
        <template v-slot:item.payer_label="{ item }">
          {{ chargePayerLabel(item) }}
        </template>
        <template v-slot:item.group_label="{ item }">
          {{ chargeGroupLabel(item) }}
        </template>
        <template v-slot:item.charge_scope="{ item }">
          {{ chargeScopeLabel(item.charge_scope) }}
        </template>
        <template v-slot:item.payment_origin="{ item }">
          {{ paymentOriginLabel(item) }}
        </template>
        <template v-slot:item.type="{ item }">
          {{ typeLabel(item.type) }}
        </template>
        <template v-slot:item.amount="{ item }">
          {{ formatCurrency(item.amount) }}
        </template>
        <template v-slot:item.opening_balance="{ item }">
          {{ formatCurrency(item.opening_balance) }}
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip small :color="statusColor(item.status)" :text-color="statusTextColor(item.status)">
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>
        <template v-slot:item.priority="{ item }">
          <v-chip small :color="priorityColor(item.priority)" :text-color="priorityTextColor(item.priority)">
            {{ priorityLabel(item.priority) }}
          </v-chip>
        </template>
        <template v-slot:item.category="{ item }">
          {{ categoryLabel(item.category) }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn v-if="hasForm" icon small :title="isDemandList ? 'Gerenciar chamado' : 'Editar'" @click="openDialog(item)">
            <v-icon size="19">{{ isDemandList ? "mdi-clipboard-edit-outline" : "mdi-pencil-outline" }}</v-icon>
          </v-btn>
        </template>
        <template v-slot:footer.prepend>
          <v-btn icon small :loading="loading" title="Atualizar" @click="load">
            <v-icon size="19">mdi-refresh</v-icon>
          </v-btn>
        </template>
        <template v-slot:no-data>
          <div class="empty-state">
            <v-icon color="primary" size="34">mdi-leaf-circle-outline</v-icon>
            <strong>Nenhum registro encontrado</strong>
            <span>Quando houver dados deste módulo, eles aparecerão aqui.</span>
          </div>
        </template>
      </BoaruaDataTable>
    </section>

    <v-dialog v-model="dialog" :max-width="isDemandChatDialog ? 1120 : 720">
      <v-card class="form-dialog">
        <v-card-title>
          <div>
            <strong>{{ form.id ? editTitle : createTitle }}</strong>
            <span>{{ form.id ? formIntro : "Cadastre as informações deste registro." }}</span>
          </div>
        </v-card-title>
        <v-card-text class="form-dialog-body">
          <div :class="['form-dialog-layout', { 'with-chat': isDemandChatDialog }]">
            <div class="form-dialog-main">
              <v-progress-linear v-if="loadingDemandDetails || loadingAssemblyDetails" indeterminate color="primary" class="mb-4" />
              <v-row dense>
                <v-col v-for="field in formFields" :key="field.name" cols="12" :md="field.cols || 6">
                  <div v-if="field.type === 'section'" class="form-section-heading">
                    <strong>{{ field.label }}</strong>
                    <span v-if="field.text">{{ field.text }}</span>
                  </div>
                  <v-alert v-else-if="field.type === 'alert'" dense text type="info" class="form-alert">
                    {{ field.text }}
                  </v-alert>
                  <v-select
                    v-else-if="field.type === 'select'"
                    v-model="form[field.name]"
                    :items="field.items"
                    :label="field.label"
                    outlined
                    dense
                    hide-details="auto"
                    :disabled="field.readonly"
                  />
                  <v-currency-field
                    v-else-if="field.type === 'currency'"
                    v-model="form[field.name]"
                    :label="field.label"
                    outlined
                    dense
                    hide-details="auto"
                    :allow-negative="field.allowNegative !== false"
                    :disabled="field.readonly"
                  />
                  <v-textarea
                    v-else-if="field.type === 'textarea'"
                    v-model="form[field.name]"
                    :label="field.label"
                    outlined
                    rows="3"
                    hide-details="auto"
                    :disabled="field.readonly"
                  />
                  <v-text-field
                    v-else
                    v-model="form[field.name]"
                    :label="field.label"
                    :type="field.type || 'text'"
                    outlined
                    dense
                    hide-details="auto"
                    :disabled="field.readonly"
                  />
                </v-col>
              </v-row>

              <section v-if="isAssemblyList && form.id" class="assembly-signatures">
                <div class="form-section-heading">
                  <strong>Adesões e presenças</strong>
                  <span>Acompanhe quem confirmou ou assinou presença nesta assembleia.</span>
                </div>

                <div class="signatures-summary">
                  <div>
                    <strong>{{ assemblySignatures.length }}</strong>
                    <span>registros</span>
                  </div>
                  <small>{{ assemblySignatureSummary }}</small>
                </div>

                <div class="signature-list">
                  <div v-for="signature in assemblySignatures" :key="signature.id" class="signature-item">
                    <div class="thread-avatar">
                      {{ initials(signatureMemberName(signature)) }}
                    </div>
                    <div>
                      <strong>{{ signatureMemberName(signature) }}</strong>
                      <span>{{ assemblySignatureTypeLabel(signature.type) }} · {{ formatDateTime(signature.signed_at) }}</span>
                    </div>
                  </div>

                  <div v-if="!assemblySignatures.length" class="thread-empty">
                    Nenhuma adesão ou assinatura registrada ainda.
                  </div>
                </div>
              </section>
            </div>

            <aside v-if="isDemandChatDialog" class="demand-chat-panel">
              <header class="chat-header">
                <div class="chat-icon">
                  <v-icon size="20">mdi-message-text-outline</v-icon>
                </div>
                <div>
                  <strong>Chat do chamado</strong>
                  <span>Respostas do administrador e do associado</span>
                </div>
              </header>

              <div class="chat-list">
                <article v-for="comment in demandComments" :key="comment.id" class="chat-message">
                  <div class="chat-avatar">
                    {{ initials(comment.user && comment.user.name) }}
                  </div>
                  <div class="chat-bubble">
                    <div class="chat-meta">
                      <strong>{{ (comment.user && comment.user.name) || "Usuário" }}</strong>
                      <span>{{ formatDateTime(comment.created_at) }}</span>
                    </div>
                    <p v-if="comment.message">{{ comment.message }}</p>
                    <a v-if="comment.resource" class="chat-attachment" :href="resourceUrl(comment.resource)" target="_blank" rel="noopener">
                      <v-icon size="17">mdi-paperclip</v-icon>
                      {{ comment.resource.name || "Anexo" }}
                    </a>
                  </div>
                </article>

                <div v-if="!demandComments.length" class="chat-empty">
                  Nenhuma resposta registrada ainda.
                </div>
              </div>

              <div class="chat-composer">
                <v-textarea
                  v-model="demandReply"
                  label="Resposta do administrador"
                  outlined
                  rows="3"
                  hide-details="auto"
                />
                <v-file-input
                  v-model="demandReplyFile"
                  label="Foto ou arquivo do atendimento"
                  prepend-icon=""
                  prepend-inner-icon="mdi-paperclip"
                  outlined
                  dense
                  hide-details="auto"
                  class="mt-3"
                />
                <v-btn
                  depressed
                  color="primary"
                  class="mt-3"
                  block
                  :loading="sendingDemandReply"
                  :disabled="!canSendDemandReply"
                  @click="sendDemandReply"
                >
                  <v-icon left size="18">mdi-send-outline</v-icon>
                  Enviar resposta
                </v-btn>
              </div>
            </aside>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">Cancelar</v-btn>
          <v-btn depressed color="primary" :loading="saving" @click="save">
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import BoaruaDataTable from "@/views/boarua/components/BoaruaDataTable.vue";
import ListFilters from "@/views/boarua/components/ListFilters.vue";
import { applyListFilters, initialListFilters, resolveDateRangeFilters } from "@/views/boarua/helpers/listFilters";

export default {
  name: "BoaruaResourceList",
  components: { BoaruaDataTable, ListFilters },
  data() {
    return {
      loading: false,
      saving: false,
      dialog: false,
      items: [],
      filterDraft: initialListFilters(),
      activeFilters: initialListFilters(),
      form: {},
      demandComments: [],
      demandReply: "",
      demandReplyFile: null,
      sendingDemandReply: false,
      loadingDemandDetails: false,
      loadingAssemblyDetails: false,
      assemblySignatures: [],
      openedQueryRecordId: null,
    };
  },
  computed: {
    title() {
      return this.$route.meta.title;
    },
    subtitle() {
      return this.$route.meta.subtitle;
    },
    endpoint() {
      return this.$route.meta.endpoint;
    },
    headers() {
      return this.$route.meta.headers || [
        { text: "ID", value: "id", width: 80 },
        { text: "Nome/Título", value: "name" },
        { text: "Status", value: "status" },
        { text: "Criado em", value: "created_at" },
      ];
    },
    tableHeaders() {
      if (!this.hasForm) return this.headers;
      const hasActions = this.headers.some((item) => item.value === "actions");
      return hasActions ? this.headers : [...this.headers, { text: "", value: "actions", sortable: false, align: "end", width: 80 }];
    },
    formFields() {
      return this.$route.meta.formFields || [];
    },
    filterFields() {
      return this.$route.meta.filterFields || [];
    },
    filterTextKeys() {
      return this.$route.meta.filterTextKeys || this.headers.map((item) => item.value).filter((value) => value && value !== "actions");
    },
    filterDateKeys() {
      return this.$route.meta.filterDateKeys || ["created_at", "updated_at", "due_date", "expires_at", "starts_at", "ends_at", "reference_month"];
    },
    filteredItems() {
      return applyListFilters(this.items, this.activeFilters, {
        textKeys: this.filterTextKeys,
        dateKeys: this.filterDateKeys,
      });
    },
    hasForm() {
      return this.formFields.length > 0;
    },
    canCreate() {
      return this.hasForm && this.$route.meta.canCreate !== false;
    },
    createButtonLabel() {
      return this.$route.meta.createButtonLabel || "Adicionar";
    },
    createTitle() {
      return this.$route.meta.createTitle || "Novo registro";
    },
    editTitle() {
      return this.$route.meta.editTitle || "Editar registro";
    },
    formIntro() {
      return this.$route.meta.formIntro || "Atualize as informações deste registro.";
    },
    updateFields() {
      return this.$route.meta.updateFields || [];
    },
    fixedPostTypes() {
      return this.$route.meta.fixedPostTypes || [];
    },
    isDemandList() {
      return this.endpoint === "/demands";
    },
    isAssemblyList() {
      return this.endpoint === "/assemblies";
    },
    isDemandChatDialog() {
      return this.isDemandList && Boolean(this.form.id);
    },
    assemblySignatureSummary() {
      if (!this.assemblySignatures.length) return "Aguardando confirmações";
      const presence = this.assemblySignatures.filter((item) => String(item.type || "presence") === "presence").length;
      return `${presence} assinatura${presence === 1 ? "" : "s"} de presença`;
    },
    canSendDemandReply() {
      return Boolean(String(this.demandReply || "").trim() || this.demandReplyFile);
    },
  },
  watch: {
    "$route.path"() {
      this.resetFilters();
      this.load();
    },
    "$route.query"() {
      this.openRequestedRecord();
    },
  },
  mounted() {
    this.load().then(() => this.openRequestedRecord());
  },
  methods: {
    statusColor(status) {
      const normalized = String(status || "").toLowerCase();
      if (["paid", "completed", "done", "active", "pago", "concluido"].includes(normalized)) return "secondary";
      if (["overdue", "late", "vencido"].includes(normalized)) return "lightImportant";
      return "offWhite";
    },
    statusTextColor(status) {
      const normalized = String(status || "").toLowerCase();
      if (["overdue", "late", "vencido"].includes(normalized)) return "red";
      return "primary";
    },
    statusLabel(status) {
      const labels = {
        scheduled: "Agendada",
        in_progress: "Em andamento",
        completed: "Concluída",
        cancelled: "Cancelada",
        active: "Ativo",
        inactive: "Inativo",
        pending: "Pendente",
        paid: "Pago",
        overdue: "Vencido",
        open: "Aberto",
        in_review: "Em análise",
        waiting_resident: "Aguardando morador",
        resolved: "Resolvido",
        closed: "Fechado",
        published: "Publicado",
        draft: "Rascunho",
      };

      return labels[status] || status || "-";
    },
    priorityColor(priority) {
      const normalized = String(priority || "").toLowerCase();
      if (["urgent", "high"].includes(normalized)) return "lightImportant";
      if (["medium"].includes(normalized)) return "warning";
      if (["low"].includes(normalized)) return "offWhite";
      return "secondary";
    },
    priorityTextColor(priority) {
      const normalized = String(priority || "").toLowerCase();
      if (["urgent", "high"].includes(normalized)) return "red";
      if (["medium"].includes(normalized)) return "white";
      return "primary";
    },
    priorityLabel(priority) {
      const labels = {
        low: "Baixa",
        normal: "Normal",
        medium: "Média",
        high: "Alta",
        urgent: "Urgente",
      };
      return labels[priority] || priority || "-";
    },
    categoryLabel(category) {
      const labels = {
        financial: "Financeiro",
        financeiro: "Financeiro",
        maintenance: "Manutenção",
        manutencao: "Manutenção",
        security: "Segurança",
        seguranca: "Segurança",
        cleaning: "Limpeza",
        limpeza: "Limpeza",
        lighting: "Iluminação",
        iluminacao: "Iluminação",
        administrative: "Administrativo",
        administrativo: "Administrativo",
        other: "Outro",
        outro: "Outro",
      };
      return labels[category] || category || "-";
    },
    resetFilters() {
      this.filterDraft = initialListFilters();
      this.activeFilters = initialListFilters();
    },
    async applyFilters(filters) {
      this.activeFilters = Object.assign(initialListFilters(), filters || {});
      await this.load();
    },
    listParams(base = {}) {
      const filters = this.activeFilters || {};
      const search = {};

      Object.keys(filters).forEach((key) => {
        const value = filters[key];
        if (["search", "period", "start_date", "end_date"].includes(key)) return;
        if (value === "" || value === null || value === undefined) return;
        if (Array.isArray(value) && !value.length) return;
        search[key] = value;
      });

      const params = Object.assign({}, base);
      const range = resolveDateRangeFilters(filters);
      if (filters.search) params.q = filters.search;
      if (range.start) params.start_date = range.start;
      if (range.end) params.end_date = range.end;
      if (Object.keys(search).length) params.search = JSON.stringify(search);

      return params;
    },
    async openDialog(item = null) {
      this.form = item ? this.formFromItem(item) : this.defaultForm();
      this.demandComments = [];
      this.demandReply = "";
      this.demandReplyFile = null;
      this.assemblySignatures = [];
      this.dialog = true;

      if (item && this.isDemandList) {
        await this.loadDemandDetails(item.id);
      }

      if (item && this.isAssemblyList) {
        await this.loadAssemblyDetails(item.id);
      }
    },
    defaultForm() {
      return this.formFields.reduce((payload, field) => {
        if (["section", "alert"].includes(field.type)) return payload;
        payload[field.name] = Object.prototype.hasOwnProperty.call(field, "default") ? field.default : "";
        return payload;
      }, {});
    },
    formFromItem(item) {
      const form = Object.assign({}, item);
      form.unitLabel = this.unitLabel(item);
      form.locationLabel = this.locationLabel(item);
      form.createdAtLabel = this.formatDateTime(item.created_at);
      this.formFields.forEach((field) => {
        if (["section", "alert"].includes(field.type)) return;
        if (field.path) {
          form[field.name] = this.valueByPath(form, field.path);
        }
        if (field.type === "datetime-local" && form[field.name]) {
          form[field.name] = this.toDatetimeLocal(form[field.name]);
        }
      });
      return form;
    },
    async loadDemandDetails(id) {
      this.loadingDemandDetails = true;
      try {
        const response = await axios.get(`${this.endpoint}/${id}`);
        const demand = response.data.content || {};
        this.form = this.formFromItem(demand);
        this.demandComments = this.normalizeComments(demand.comments || []);
        try {
          await this.loadDemandComments(id);
        } catch (error) {
          // The demand detail can still be managed without the thread if comments fail to refresh.
        }
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : "Não foi possível carregar o chamado");
      } finally {
        this.loadingDemandDetails = false;
      }
    },
    async loadAssemblyDetails(id) {
      this.loadingAssemblyDetails = true;
      try {
        const response = await axios.get(`${this.endpoint}/${id}`);
        const assembly = response.data.content || {};
        this.form = this.formFromItem(assembly);
        this.assemblySignatures = this.normalizeComments(assembly.signatures || []);
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : "Não foi possível carregar a assembleia");
      } finally {
        this.loadingAssemblyDetails = false;
      }
    },
    async openRequestedRecord() {
      const recordId = this.$route.query.record_id || this.$route.query.assembly_id;
      if (!recordId || String(recordId) === String(this.openedQueryRecordId)) return;
      if (!this.items.length) return;

      const item = this.items.find((row) => String(row.id) === String(recordId)) || { id: recordId };
      this.openedQueryRecordId = recordId;
      await this.openDialog(item);
    },
    normalizeComments(comments) {
      let list = [];
      if (comments && Array.isArray(comments.rows)) list = comments.rows;
      else if (comments && Array.isArray(comments.data)) list = comments.data;
      else if (Array.isArray(comments)) list = comments;

      return list.slice().sort((a, b) => {
        const left = new Date(a.created_at || a.createdAt || 0).getTime();
        const right = new Date(b.created_at || b.createdAt || 0).getTime();
        return left - right;
      });
    },
    signatureMemberName(signature = {}) {
      const member = signature.member || {};
      const user = member.user || {};
      return user.name || member.name || "Morador";
    },
    assemblySignatureTypeLabel(type) {
      const labels = {
        presence: "Presença",
        adhesion: "Adesão",
        confirmation: "Confirmação",
      };
      return labels[type] || "Presença";
    },
    async sendDemandReply() {
      if (!this.canSendDemandReply || !this.form.id) return;

      this.sendingDemandReply = true;
      try {
        let resourceId = null;

        if (this.demandReplyFile) {
          const formData = new FormData();
          formData.append("file", this.demandReplyFile);
          formData.append("name", this.demandReplyFile.name || "Anexo do chamado");

          const upload = await axios.post("/resources", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          resourceId = upload.data.content && upload.data.content.id;
        }

        const response = await axios.post(`${this.endpoint}/${this.form.id}/comments`, {
          message: String(this.demandReply || "").trim(),
          resource_id: resourceId,
        });

        this.demandComments.push(response.data.content);
        this.demandReply = "";
        this.demandReplyFile = null;
        try {
          await this.loadDemandComments(this.form.id);
        } catch (error) {
          // Keep the optimistic response visible when the refresh endpoint is temporarily unavailable.
        }
        this.notifySuccess("Resposta enviada");
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : "Não foi possível enviar a resposta");
      } finally {
        this.sendingDemandReply = false;
      }
    },
    async loadDemandComments(id) {
      const response = await axios.get(`${this.endpoint}/${id}/comments`);
      this.demandComments = this.normalizeComments(response.data.content || []);
    },
    toDatetimeLocal(value) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      const pad = (number) => String(number).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
    async save() {
      this.saving = true;
      try {
        const payload = this.savePayload();
        if (payload.id) {
          await axios.patch(`${this.endpoint}/${payload.id}`, payload);
        } else {
          await axios.post(this.endpoint, payload);
        }
        this.dialog = false;
        await this.load();
        this.notifySuccess("Registro salvo");
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : "Não foi possível salvar");
      } finally {
        this.saving = false;
      }
    },
    savePayload() {
      if (!this.updateFields.length) return this.normalizePayloadCurrencies(Object.assign({}, this.form));

      const payload = { id: this.form.id };
      this.updateFields.forEach((field) => {
        payload[field] = this.form[field];
      });
      return this.normalizePayloadCurrencies(payload);
    },
    normalizePayloadCurrencies(payload) {
      this.formFields.forEach((field) => {
        if (field.type !== "currency" || !Object.prototype.hasOwnProperty.call(payload, field.name)) return;
        payload[field.name] = this.parseCurrencyValue(payload[field.name]);
      });
      return payload;
    },
    parseCurrencyValue(value) {
      if (value === "" || value === null || value === undefined) return 0;
      if (typeof value === "number") return Number.isFinite(value) ? value : 0;
      const cleaned = String(value)
        .replace(/\s/g, "")
        .replace(/R\$/gi, "")
        .replace(/[^0-9,.-]/g, "");
      const lastComma = cleaned.lastIndexOf(",");
      const lastDot = cleaned.lastIndexOf(".");
      let normalized = cleaned;
      if (lastComma >= 0 && lastDot >= 0) {
        normalized = lastComma > lastDot
          ? cleaned.replace(/\./g, "").replace(",", ".")
          : cleaned.replace(/,/g, "");
      } else if (lastComma >= 0) {
        normalized = cleaned.replace(/\./g, "").replace(",", ".");
      }
      const amount = Number(normalized || 0);
      return Number.isFinite(amount) ? amount : 0;
    },
    valueByPath(item, path) {
      return String(path || "").split(".").reduce((value, key) => {
        if (value === null || value === undefined) return "";
        return value[key];
      }, item) || "";
    },
    unitLabel(item = {}) {
      const unit = item.unit || {};
      const parts = [
        unit.street || item.street,
        unit.number || item.number,
        unit.complement || item.complement,
      ].filter(Boolean);
      return parts.length ? parts.join(", ") : "-";
    },
    locationLabel(item = {}) {
      const parts = [item.street, item.number].filter(Boolean);
      if (parts.length) return parts.join(", ");
      return this.unitLabel(item);
    },
    formatDateTime(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "-";
      return date.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    formatDate(value) {
      if (!value) return "-";
      const text = String(value);
      const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) return `${match[3]}/${match[2]}/${match[1]}`;

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return text;
      return date.toLocaleDateString("pt-BR", { timeZone: "UTC" });
    },
    formatReferenceMonth(value) {
      if (!value) return "-";
      const match = String(value).match(/^(\d{4})-(\d{2})/);
      if (match) return `${match[2]}/${match[1]}`;
      return value;
    },
    formatCurrency(value) {
      const amount = Number(value);
      if (!Number.isFinite(amount)) return "-";
      return amount.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    chargeUnitLabel(item = {}) {
      const member = item.member || item.payer || {};
      const units = this.rowsFromRelation(member.units);
      if (!units.length) return "-";
      return units.map((unit) => this.unitLabel(unit)).join(" | ");
    },
    chargePayerLabel(item = {}) {
      const payer = item.payer || item.member || {};
      const user = payer.user || {};
      return user.name || payer.name || payer.full_name || "-";
    },
    chargeGroupLabel(item = {}) {
      const group = item.contributionGroup || item.contribution_group || {};
      return group.name || "-";
    },
    chargeScopeLabel(value) {
      const labels = {
        member: "Por residência",
        group: "Centralizado",
      };
      return labels[value] || value || "-";
    },
    paymentOriginLabel(item = {}) {
      if (item.payment_origin_label) return item.payment_origin_label;
      const labels = {
        reconciliation: "Conciliação",
        system: "Sistema",
      };
      return labels[item.payment_origin] || "-";
    },
    typeLabel(value) {
      const labels = {
        checking: "Conta corrente",
        cash: "Caixa",
        savings: "Poupança",
        reserve: "Reserva",
        other: "Outro",
      };
      return labels[value] || value || "-";
    },
    rowsFromRelation(value) {
      if (!value) return [];
      if (Array.isArray(value)) return value;
      if (Array.isArray(value.rows)) return value.rows;
      if (Array.isArray(value.data)) return value.data;
      return [];
    },
    initials(name) {
      return String(name || "U")
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.charAt(0))
        .join("")
        .toUpperCase();
    },
    resourceUrl(resource = {}) {
      return resource.file_url || resource.url || "#";
    },
    rowsFromResponse(response) {
      return response.data.content.data || response.data.content || [];
    },
    async load() {
      this.loading = true;
      try {
        if (this.fixedPostTypes.length) {
          const requests = this.fixedPostTypes.map((postType) =>
            axios.get(this.endpoint, { params: this.listParams({ limit: 50, post_type: postType }) })
          );
          const responses = await Promise.all(requests);
          this.items = responses
            .flatMap((response) => this.rowsFromResponse(response))
            .filter((item) => this.fixedPostTypes.includes(item.post_type));
          return;
        }

        const response = await axios.get(this.endpoint, { params: this.listParams({ limit: 50 }) });
        this.items = this.rowsFromResponse(response);
      } finally {
        this.loading = false;
      }
    },
    notifySuccess(message) {
      this.$dialog && this.$dialog.notify && this.$dialog.notify.success(message, { position: "top-right", timeout: 3000 });
    },
    notifyError(message) {
      this.$dialog && this.$dialog.notify && this.$dialog.notify.error(message, { position: "top-right", timeout: 4000 });
    },
  },
};
</script>

<style scoped lang="scss">
.table-panel {
  background: #ffffff;
  border: 1px solid #e1e7e0;
  border-radius: 8px;
}

.resource-toolbar {
  margin-bottom: 14px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;

  h2 {
    margin: 0;
    color: #111827;
    font-size: 20px;
  }

  p {
    margin: 3px 0 0;
    color: #66736b;
    font-size: 13px;
  }
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.table-panel {
  overflow: hidden;
}

.boarua-table::v-deep {
  th {
    color: #66736b !important;
    font-size: 12px !important;
    text-transform: uppercase;
  }

  td {
    color: #111827;
  }
}

.empty-state {
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;

  strong {
    color: #111827;
  }

  span {
    color: #66736b;
    font-size: 13px;
  }
}

.form-section-heading {
  margin-top: 10px;
  padding-top: 18px;
  border-top: 1px solid #e1e7e0;

  strong,
  span {
    display: block;
  }

  strong {
    color: #111827;
    font-size: 15px;
    font-weight: 900;
  }

  span {
    margin-top: 4px;
    color: #66736b;
    font-size: 13px;
    line-height: 1.4;
  }
}

.form-alert {
  margin-bottom: 0;
}

.form-dialog-body {
  overflow-x: hidden;
}

.form-dialog-layout {
  display: block;

  &.with-chat {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 360px;
    gap: 18px;
    align-items: stretch;
  }
}

.form-dialog-main {
  min-width: 0;
}

.demand-chat-panel {
  min-height: 100%;
  display: grid;
  grid-template-rows: auto minmax(180px, 1fr) auto;
  overflow: hidden;
  background: #f8fbf7;
  border: 1px solid #dfe9df;
  border-radius: 8px;
}

.chat-header {
  padding: 14px;
  display: grid;
  grid-template-columns: 38px 1fr;
  gap: 10px;
  align-items: center;
  border-bottom: 1px solid #dfe9df;

  strong,
  span {
    display: block;
  }

  strong {
    color: #111827;
    font-size: 15px;
    font-weight: 900;
  }

  span {
    margin-top: 2px;
    color: #66736b;
    font-size: 12px;
    line-height: 1.35;
  }
}

.chat-icon {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e9f6ee;
  color: #007f3d;
}

.chat-list {
  padding: 14px;
  display: grid;
  align-content: start;
  gap: 12px;
  max-height: 430px;
  overflow-y: auto;
}

.chat-message {
  display: grid;
  grid-template-columns: 32px 1fr;
  gap: 9px;
}

.chat-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #007f3d;
  color: #ffffff;
  font-size: 11px;
  font-weight: 900;
}

.chat-bubble {
  min-width: 0;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #e1e7e0;
  border-radius: 8px;

  p {
    margin: 7px 0 0;
    color: #111827;
    font-size: 13px;
    line-height: 1.45;
    white-space: pre-wrap;
  }
}

.chat-meta {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;

  strong {
    color: #111827;
    font-size: 12px;
    line-height: 1.3;
  }

  span {
    flex: 0 0 auto;
    color: #66736b;
    font-size: 11px;
    line-height: 1.3;
  }
}

.chat-attachment {
  margin-top: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #007f3d;
  font-size: 12px;
  font-weight: 800;
  text-decoration: none;
}

.chat-empty {
  padding: 14px;
  color: #66736b;
  font-size: 13px;
  text-align: center;
  border: 1px dashed #cfdccd;
  border-radius: 8px;
}

.chat-composer {
  padding: 14px;
  border-top: 1px solid #dfe9df;
  background: #ffffff;
}

.assembly-signatures {
  margin-top: 18px;
  padding-top: 4px;
}

.signatures-summary {
  margin: 12px 0;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  background: #f8fbf7;
  border: 1px solid #dfe9df;
  border-radius: 8px;

  strong,
  span {
    display: block;
  }

  strong {
    color: #007f3d;
    font-size: 24px;
    font-weight: 900;
    line-height: 1;
  }

  span,
  small {
    color: #66736b;
    font-size: 13px;
  }
}

.signature-list {
  display: grid;
  gap: 10px;
}

.signature-item {
  display: grid;
  grid-template-columns: 34px 1fr;
  gap: 10px;
  padding: 12px;
  border: 1px solid #dfe9df;
  border-radius: 8px;

  strong,
  span {
    display: block;
  }

  strong {
    color: #111827;
    font-size: 13px;
  }

  span {
    margin-top: 2px;
    color: #66736b;
    font-size: 12px;
  }
}

.thread-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #007f3d;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
}

.thread-empty {
  padding: 14px;
  background: #f8fbf7;
  border: 1px dashed #cfdccd;
  border-radius: 8px;
  color: #66736b;
  font-size: 13px;
}

@media (max-width: 780px) {
  .resource-toolbar,
  .toolbar-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .form-dialog-layout.with-chat {
    grid-template-columns: 1fr;
  }

  .chat-list {
    max-height: 320px;
  }

  .chat-meta {
    display: block;

    span {
      display: block;
      margin-top: 2px;
    }
  }
}
</style>
